import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { Icon, tokens } from '@/design';
import { LayoutGraphic } from './LayoutGraphic';
import { LayoutNorthArrow, NORTH_ARROW_STYLES } from './LayoutNorthArrow';
import { LayoutScaleBar } from './LayoutScaleBar';
import { LegendStyleThumbnail } from './LegendStyleThumbnail';
import { TableFrameStyleThumbnail } from './TableFrameStyleThumbnail';
import { createLayoutGraphicElement, DEFAULT_SCALE_BAR_TEXT_SYMBOL } from './layoutDocument';
import { LAYOUT_GRAPHIC_CATEGORIES, LAYOUT_GRAPHIC_TOOLS } from './layoutGraphicTools';
import {
  legendStyleGroups,
  type LegendStyleCategory,
} from './legendStyles';
import { TABLE_FRAME_STYLES } from './tableFrameStyles';
import { PageSizePicker } from './PageSizePicker';
import {
  layoutPictureSourceFromFile,
  layoutPictureSourceFromUrl,
  PICTURE_ACCEPT,
} from './layoutPicture';
import {
  DUAL_SCALE_BAR_STYLES,
  SCALE_BAR_STYLES,
  SCALE_BAR_UNIT_GROUPS,
  scaleBarStyleMeta,
  scaleBarStyleLabel,
} from './scaleBarStyles';
import type {
  NorthArrowStyleId,
  LegendStyleId,
  PagePresetId,
  PageUnits,
  ScaleBarElement,
  ScaleBarStyleId,
  ScaleBarUnits,
  TableFrameStyleId,
  LayoutGraphicType,
  LayoutPictureSource,
} from './layoutTypes';

export interface LayoutInsertBarProps {
  readonly paperPreset: PagePresetId;
  readonly paperWidthMm: number;
  readonly paperHeightMm: number;
  readonly paperUnits: PageUnits;
  readonly onPaperPreset: (preset: Exclude<PagePresetId, 'custom'>) => void;
  readonly onCustomPageSize: (widthMm: number, heightMm: number, units: PageUnits) => void;
  readonly pageZoomPercent: number;
  readonly onPageZoomPercent: (percent: number) => void;
  readonly onInsertNorthArrow: (styleId: NorthArrowStyleId) => void;
  readonly onInsertScaleBar: (styleId: ScaleBarStyleId, units: ScaleBarUnits) => void;
  readonly onInsertLegend: (styleId: LegendStyleId) => void;
  readonly onInsertTableFrame: (styleId: TableFrameStyleId) => void;
  readonly activeGraphicTool: LayoutGraphicType | null;
  readonly keepGraphicToolActive: boolean;
  readonly onSelectGraphicTool: (tool: LayoutGraphicType) => void;
  readonly onKeepGraphicToolActive: (keep: boolean) => void;
  readonly onPictureSource: (source: LayoutPictureSource) => void;
  readonly onMatchMapView: () => void;
  readonly onExportPng: () => void;
  readonly onPrint: () => void;
}

/**
 * Gallery thumbnails are drawn by the real surround components at a fixed
 * scale, so what the picker shows is what the page gets. 1:150 000 over this
 * width lands on a round 4 km / 2 mi bar.
 */
const PREVIEW_SCALE_DENOMINATOR = 150_000;
const PREVIEW_WIDTH_MM = 40;

/** Gallery panel widths, kept here so the overflow maths matches the style. */
const NORTH_PANEL_WIDTH = 300;
const SCALE_PANEL_WIDTH = 560;
const LEGEND_PANEL_WIDTH = 340;
const TABLE_PANEL_WIDTH = 760;
const GRAPHICS_PANEL_WIDTH = 620;
const PICTURE_PANEL_WIDTH = 280;
const TABLE_TILE_MIN_WIDTH = 220;
const PANEL_EDGE_GAP = tokens.space[4];

type OpenGallery = 'north' | 'scale' | 'legend' | 'tableFrame' | 'graphics' | 'picture' | null;
type ScaleGalleryFilter = 'all' | ScaleBarUnits;
type LegendGalleryFilter = 'all' | LegendStyleCategory;

function previewScaleBar(
  styleId: ScaleBarStyleId,
  units: ScaleBarUnits,
): ScaleBarElement {
  const meta = scaleBarStyleMeta(styleId);
  const dualUnits = meta.defaultUnits;
  return {
    id: 'scale-bar-preview',
    type: 'scaleBar',
    mapFrameId: 'preview-frame',
    styleId,
    units,
    name: meta.dual ? 'Dual Scale Bar' : 'Scale Bar',
    visible: true,
    locked: false,
    computeAtCenter: true,
    upper: {
      unit: dualUnits?.[0] ?? (units === 'imperial' ? 'miles' : 'kilometers'),
      divisionValue: null,
      divisions: meta.divisions,
      subdivisions: 1,
    },
    lower: dualUnits
      ? {
          unit: dualUnits[1],
          divisionValue: null,
          divisions: meta.divisions,
          subdivisions: 1,
        }
      : null,
    horizontalOffsetPt: 0,
    verticalOffsetPt: 0,
    fittingStrategy: 'adjustDivision',
    fixedBarWidthMm: PREVIEW_WIDTH_MM * 0.68,
    textSymbol: DEFAULT_SCALE_BAR_TEXT_SYMBOL,
    rect: {
      x: 0,
      y: 0,
      width: PREVIEW_WIDTH_MM,
      height: meta.dual ? 14 : 9,
    },
  };
}

/**
 * Galleries anchor to their button, so the wide ones (table frames, scale bars)
 * run off the right edge when the button sits late in the toolbar. Measuring on
 * open lets the panel slide left just enough to stay on screen.
 */
function usePanelShift(open: boolean, width: number, marginPx: number) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [shiftLeftPx, setShiftLeftPx] = useState(0);

  useLayoutEffect(() => {
    if (!open) {
      setShiftLeftPx(0);
      return undefined;
    }
    const measure = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const { left } = anchor.getBoundingClientRect();
      const overflow = left + width + marginPx - window.innerWidth;
      const room = Math.max(left - marginPx, 0);
      setShiftLeftPx(overflow > 0 ? Math.min(overflow, room) : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, width, marginPx]);

  return { anchorRef, shiftLeftPx };
}

export function LayoutInsertBar({
  paperPreset,
  paperWidthMm,
  paperHeightMm,
  paperUnits,
  onPaperPreset,
  onCustomPageSize,
  pageZoomPercent,
  onPageZoomPercent,
  onInsertNorthArrow,
  onInsertScaleBar,
  onInsertLegend,
  onInsertTableFrame,
  activeGraphicTool,
  keepGraphicToolActive,
  onSelectGraphicTool,
  onKeepGraphicToolActive,
  onPictureSource,
  onMatchMapView,
  onExportPng,
  onPrint,
}: LayoutInsertBarProps) {
  const [openGallery, setOpenGallery] = useState<OpenGallery>(null);
  const [scaleFilter, setScaleFilter] = useState<ScaleGalleryFilter>('all');
  const [legendFilter, setLegendFilter] = useState<LegendGalleryFilter>('all');
  const [pictureUrl, setPictureUrl] = useState('');
  const [pictureError, setPictureError] = useState<string | null>(null);
  const pictureFileRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const northPanel = usePanelShift(openGallery === 'north', NORTH_PANEL_WIDTH, PANEL_EDGE_GAP);
  const scalePanel = usePanelShift(openGallery === 'scale', SCALE_PANEL_WIDTH, PANEL_EDGE_GAP);
  const legendPanel = usePanelShift(openGallery === 'legend', LEGEND_PANEL_WIDTH, PANEL_EDGE_GAP);
  const tablePanel = usePanelShift(
    openGallery === 'tableFrame',
    TABLE_PANEL_WIDTH,
    PANEL_EDGE_GAP,
  );
  const graphicsPanel = usePanelShift(
    openGallery === 'graphics',
    GRAPHICS_PANEL_WIDTH,
    PANEL_EDGE_GAP,
  );
  const picturePanel = usePanelShift(
    openGallery === 'picture',
    PICTURE_PANEL_WIDTH,
    PANEL_EDGE_GAP,
  );

  useEffect(() => {
    if (!openGallery) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpenGallery(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenGallery(null);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [openGallery]);

  const galleryStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: tokens.space[1],
  };
  const chip = (active?: boolean) => ({
    minHeight: tokens.density.touchMin,
    padding: `0 ${tokens.space[3]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: active ? tokens.color.accent.subtle : tokens.color.surface[0],
    color: tokens.color.ink.primary,
    cursor: 'pointer' as const,
    fontSize: tokens.font.size.xs,
    fontFamily: tokens.font.family.ui,
  });
  const groupLabel: CSSProperties = {
    fontSize: tokens.font.size.xs,
    color: tokens.color.ink.muted,
    marginBottom: tokens.space[1],
  };
  const menuPanel = (width: number, shiftLeftPx = 0): CSSProperties => ({
    position: 'absolute',
    zIndex: tokens.zIndex.tooltip,
    top: `calc(100% + ${tokens.space[1]}px)`,
    left: -shiftLeftPx,
    width: `min(${width}px, calc(100vw - ${tokens.space[4] * 2}px))`,
    maxHeight: 460,
    overflowY: 'auto',
    padding: tokens.space[2],
    background: tokens.color.surface[1],
    border: tokens.border.thin,
    borderRadius: tokens.radius.lg,
    boxShadow: tokens.elevation[2],
  });
  /** Thumbnails sit on paper so the cartographic ink reads correctly. */
  const tile: CSSProperties = {
    padding: tokens.space[2],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.space[1],
    cursor: 'pointer',
    background: tokens.color.canvas.light,
    color: tokens.color.ink.inverse,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.xs,
    textAlign: 'center',
  };

  const toggle = (gallery: Exclude<OpenGallery, null>) =>
    setOpenGallery((open) => (open === gallery ? null : gallery));

  return (
    <div
      ref={rootRef}
      data-testid="layout-insert-bar"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: tokens.space[4],
        padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
        borderBottom: tokens.border.thin,
        background: tokens.color.surface[1],
      }}
    >
      <div>
        <div style={groupLabel}>Page</div>
        <PageSizePicker
          value={paperPreset}
          widthMm={paperWidthMm}
          heightMm={paperHeightMm}
          units={paperUnits}
          onPreset={onPaperPreset}
          onCustom={onCustomPageSize}
        />
      </div>
      <div>
        <div style={groupLabel}>Map frame</div>
        <button
          type="button"
          aria-label="Match map view"
          onClick={onMatchMapView}
          style={chip()}
        >
          <Icon name="my_location" size={16} decorative /> Match map view
        </button>
      </div>
      <div>
        <div style={groupLabel}>Sheet zoom</div>
        <input
          aria-label="Layout page zoom"
          type="range"
          min={5}
          max={150}
          value={pageZoomPercent}
          onChange={(event) => onPageZoomPercent(Number(event.target.value))}
        />
        <span style={{ fontSize: tokens.font.size.xs, marginLeft: tokens.space[2], color: tokens.color.ink.secondary }}>
          {pageZoomPercent}%
        </span>
      </div>
      <div>
        <div style={groupLabel}>North arrows</div>
        <div ref={northPanel.anchorRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Open north arrow gallery"
            aria-haspopup="menu"
            aria-expanded={openGallery === 'north'}
            onClick={() => toggle('north')}
            style={{
              ...chip(openGallery === 'north'),
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
              paddingLeft: tokens.space[2],
            }}
          >
            <span
              aria-hidden
              style={{
                width: 25,
                height: 32,
                display: 'inline-flex',
                padding: 2,
                background: tokens.color.canvas.light,
                borderRadius: tokens.radius.sm,
              }}
            >
              <LayoutNorthArrow styleId="arcgisNorth1" bearingDeg={0} />
            </span>
            North arrow
            <Icon
              name={openGallery === 'north' ? 'expand_less' : 'expand_more'}
              size={16}
              decorative
            />
          </button>
          {openGallery === 'north' ? (
            <div
              role="menu"
              aria-label="North arrow styles"
              data-testid="north-arrow-gallery"
              style={menuPanel(NORTH_PANEL_WIDTH, northPanel.shiftLeftPx)}
            >
              <div style={{ ...groupLabel, color: tokens.color.ink.secondary }}>Recent</div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: tokens.space[1],
                  paddingBottom: tokens.space[3],
                  borderBottom: tokens.border.thin,
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  title="ArcGIS North 2 north arrow"
                  aria-label="Insert recent ArcGIS North 2 north arrow"
                  onClick={() => {
                    onInsertNorthArrow('arcgisNorth2');
                    setOpenGallery(null);
                  }}
                  style={{ ...tile, minHeight: 104 }}
                >
                  <span aria-hidden style={{ display: 'block', width: 50, height: 68 }}>
                    <LayoutNorthArrow styleId="arcgisNorth2" bearingDeg={0} />
                  </span>
                  <span>ArcGIS North 2</span>
                </button>
              </div>
              <div
                style={{
                  ...groupLabel,
                  color: tokens.color.ink.secondary,
                  marginTop: tokens.space[3],
                }}
              >
                North Arrows
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: tokens.space[1],
                }}
              >
                {NORTH_ARROW_STYLES.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    role="menuitem"
                    title={`${style.label} north arrow`}
                    aria-label={`Insert ${style.label} north arrow`}
                    onClick={() => {
                      onInsertNorthArrow(style.id);
                      setOpenGallery(null);
                    }}
                    style={{ ...tile, minHeight: 104 }}
                  >
                    <span aria-hidden style={{ display: 'block', width: 50, height: 68 }}>
                      <LayoutNorthArrow styleId={style.id} bearingDeg={0} />
                    </span>
                    <span>{style.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <div style={groupLabel}>Scale bars</div>
        <div ref={scalePanel.anchorRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Open scale bar gallery"
            aria-haspopup="menu"
            aria-expanded={openGallery === 'scale'}
            onClick={() => toggle('scale')}
            style={{
              ...chip(openGallery === 'scale'),
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
              paddingLeft: tokens.space[2],
            }}
          >
            <span
              aria-hidden
              style={{
                width: 54,
                height: 32,
                display: 'inline-flex',
                padding: 2,
                background: tokens.color.canvas.light,
                borderRadius: tokens.radius.sm,
              }}
            >
              <LayoutScaleBar
                styleId="alternating1"
                units="metric"
                scaleDenominator={PREVIEW_SCALE_DENOMINATOR}
                maxWidthMm={PREVIEW_WIDTH_MM}
              />
            </span>
            Scale bar
            <Icon
              name={openGallery === 'scale' ? 'expand_less' : 'expand_more'}
              size={16}
              decorative
            />
          </button>
          {openGallery === 'scale' ? (
            <div
              role="menu"
              aria-label="Scale bar styles"
              data-testid="scale-bar-gallery"
              style={menuPanel(SCALE_PANEL_WIDTH, scalePanel.shiftLeftPx)}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  paddingBottom: tokens.space[2],
                  borderBottom: tokens.border.thin,
                }}
              >
                <select
                  aria-label="Filter scale bar styles"
                  value={scaleFilter}
                  onChange={(event) =>
                    setScaleFilter(event.target.value as ScaleGalleryFilter)
                  }
                  style={{
                    minHeight: tokens.density.rowDense,
                    padding: `0 ${tokens.space[2]}px`,
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.input,
                    background: tokens.color.surface[0],
                    color: tokens.color.ink.primary,
                    fontFamily: tokens.font.family.ui,
                  }}
                >
                  <option value="all">All</option>
                  <option value="metric">Metric</option>
                  <option value="imperial">Imperial</option>
                  <option value="dual">Dual Scale Bars</option>
                </select>
              </div>

              {SCALE_BAR_UNIT_GROUPS
                .filter(
                  (group) =>
                    scaleFilter === 'all' || scaleFilter === group.units,
                )
                .map((group) => (
                  <div
                    key={group.units}
                    role="group"
                    aria-label={`${group.label} scale bars`}
                    style={{
                      padding: `${tokens.space[3]}px 0`,
                      borderBottom: tokens.border.thin,
                    }}
                  >
                    <div
                      style={{
                        ...groupLabel,
                        color: tokens.color.ink.primary,
                        fontWeight: tokens.font.weight.semibold,
                      }}
                    >
                      {group.label}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: tokens.space[2],
                      }}
                    >
                      {SCALE_BAR_STYLES.map((style) => {
                        const preview = previewScaleBar(style.id, group.units);
                        return (
                          <button
                            key={`${group.units}-${style.id}`}
                            type="button"
                            role="menuitem"
                            title={scaleBarStyleLabel(style.id, group.units)}
                            aria-label={`Insert ${scaleBarStyleLabel(style.id, group.units)} scale bar`}
                            onClick={() => {
                              onInsertScaleBar(style.id, group.units);
                              setOpenGallery(null);
                            }}
                            style={{ ...tile, minHeight: 82, alignItems: 'stretch' }}
                          >
                            <span aria-hidden style={{ display: 'block', height: 42 }}>
                              <LayoutScaleBar
                                styleId={style.id}
                                units={group.units}
                                scaleDenominator={PREVIEW_SCALE_DENOMINATOR}
                                maxWidthMm={PREVIEW_WIDTH_MM}
                                element={preview}
                              />
                            </span>
                            <span>{style.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

              {scaleFilter === 'all' || scaleFilter === 'dual' ? (
                <div
                  role="group"
                  aria-label="Dual Scale Bars"
                  style={{ padding: `${tokens.space[3]}px 0` }}
                >
                  <div
                    style={{
                      ...groupLabel,
                      color: tokens.color.ink.primary,
                      fontWeight: tokens.font.weight.semibold,
                    }}
                  >
                    Dual Scale Bars
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: tokens.space[2],
                    }}
                  >
                    {DUAL_SCALE_BAR_STYLES.map((style) => {
                      const preview = previewScaleBar(style.id, 'dual');
                      return (
                        <button
                          key={style.id}
                          type="button"
                          role="menuitem"
                          title={style.label}
                          aria-label={`Insert ${style.label} scale bar`}
                          onClick={() => {
                            onInsertScaleBar(style.id, 'dual');
                            setOpenGallery(null);
                          }}
                          style={{ ...tile, minHeight: 94, alignItems: 'stretch' }}
                        >
                          <span aria-hidden style={{ display: 'block', height: 52 }}>
                            <LayoutScaleBar
                              styleId={style.id}
                              units="dual"
                              scaleDenominator={PREVIEW_SCALE_DENOMINATOR}
                              maxWidthMm={PREVIEW_WIDTH_MM}
                              element={preview}
                            />
                          </span>
                          <span>{style.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <button
                type="button"
                aria-label="Show all scale bar styles"
                onClick={() => setScaleFilter('all')}
                style={{
                  ...chip(scaleFilter === 'all'),
                  width: '100%',
                  justifyContent: 'flex-start',
                  borderRadius: 0,
                  borderLeft: 0,
                  borderRight: 0,
                  borderBottom: 0,
                  textAlign: 'left',
                }}
              >
                <Icon name="layers" size={16} decorative /> More…
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <div style={groupLabel}>Legend</div>
        <div ref={legendPanel.anchorRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Open legend gallery"
            aria-haspopup="menu"
            aria-expanded={openGallery === 'legend'}
            onClick={() => toggle('legend')}
            style={{
              ...chip(openGallery === 'legend'),
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
            }}
          >
            <Icon name="layers" size={16} decorative />
            Legend
            <Icon
              name={openGallery === 'legend' ? 'expand_less' : 'expand_more'}
              size={16}
              decorative
            />
          </button>
          {openGallery === 'legend' ? (
            <div
              role="menu"
              aria-label="Legend styles"
              data-testid="legend-gallery"
              style={menuPanel(LEGEND_PANEL_WIDTH, legendPanel.shiftLeftPx)}
            >
              <label
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  alignItems: 'center',
                  gap: tokens.space[2],
                  paddingBottom: tokens.space[2],
                  borderBottom: tokens.border.thin,
                  fontSize: tokens.font.size.sm,
                }}
              >
                <span>Filter legend styles</span>
                <select
                  aria-label="Filter legend styles"
                  value={legendFilter}
                  onChange={(event) =>
                    setLegendFilter(event.target.value as LegendGalleryFilter)
                  }
                  style={chip()}
                >
                  <option value="all">All</option>
                  <option value="standard">Legends</option>
                  <option value="large">Large Text</option>
                  <option value="medium">Medium Text</option>
                </select>
              </label>
              {legendStyleGroups()
                .filter(
                  (group) =>
                    legendFilter === 'all' || legendFilter === group.id,
                )
                .map((group) => (
                  <div
                    key={group.id}
                    role="group"
                    aria-label={group.label}
                    style={{
                      padding: `${tokens.space[3]}px 0`,
                      borderBottom: tokens.border.thin,
                    }}
                  >
                    <div
                      style={{
                        ...groupLabel,
                        color: tokens.color.ink.primary,
                        fontWeight: tokens.font.weight.semibold,
                      }}
                    >
                      {group.label}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: tokens.space[2],
                      }}
                    >
                      {group.styles.map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          role="menuitem"
                          title={style.label}
                          aria-label={`Insert ${style.label}`}
                          onClick={() => {
                            onInsertLegend(style.id);
                            setOpenGallery(null);
                          }}
                          style={{ ...tile, minHeight: 112 }}
                        >
                          <LegendStyleThumbnail styleId={style.id} />
                          <span>{style.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              <button
                type="button"
                aria-label="Show all legend styles"
                onClick={() => setLegendFilter('all')}
                style={{
                  ...chip(legendFilter === 'all'),
                  width: '100%',
                  justifyContent: 'flex-start',
                  borderRadius: 0,
                  borderLeft: 0,
                  borderRight: 0,
                  borderBottom: 0,
                  textAlign: 'left',
                }}
              >
                <Icon name="layers" size={16} decorative /> More…
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <div style={groupLabel}>Table frame</div>
        <div ref={tablePanel.anchorRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Open table frame gallery"
            aria-haspopup="menu"
            aria-expanded={openGallery === 'tableFrame'}
            onClick={() => toggle('tableFrame')}
            style={{
              ...chip(openGallery === 'tableFrame'),
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
            }}
          >
            <Icon name="table" size={16} decorative />
            Table frame
            <Icon
              name={openGallery === 'tableFrame' ? 'expand_less' : 'expand_more'}
              size={16}
              decorative
            />
          </button>
          {openGallery === 'tableFrame' ? (
            <div
              role="menu"
              aria-label="Table frame styles"
              data-testid="table-frame-gallery"
              style={menuPanel(TABLE_PANEL_WIDTH, tablePanel.shiftLeftPx)}
            >
              <div
                style={{
                  ...groupLabel,
                  color: tokens.color.ink.primary,
                  fontWeight: tokens.font.weight.semibold,
                }}
              >
                Table Frames
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${TABLE_TILE_MIN_WIDTH}px, 1fr))`,
                  gap: tokens.space[2],
                }}
              >
                {TABLE_FRAME_STYLES.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    role="menuitem"
                    title={style.label}
                    aria-label={`Insert ${style.label} table frame`}
                    onClick={() => {
                      onInsertTableFrame(style.id);
                      setOpenGallery(null);
                    }}
                    style={{ ...tile, minHeight: 180 }}
                  >
                    <TableFrameStyleThumbnail styleId={style.id} />
                    <span>{style.label}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                aria-label="Show all table frame styles"
                style={{
                  ...chip(true),
                  width: '100%',
                  justifyContent: 'flex-start',
                  marginTop: tokens.space[2],
                  borderRadius: 0,
                  borderLeft: 0,
                  borderRight: 0,
                  borderBottom: 0,
                  textAlign: 'left',
                }}
              >
                <Icon name="description" size={16} decorative /> More…
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <div style={groupLabel}>Graphics and Text</div>
        <div ref={graphicsPanel.anchorRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Open graphics and text gallery"
            aria-haspopup="dialog"
            aria-expanded={openGallery === 'graphics'}
            onClick={() => toggle('graphics')}
            style={{ ...chip(openGallery === 'graphics'), display: 'flex', alignItems: 'center', gap: tokens.space[2] }}
          >
            <Icon name="edit" size={16} decorative />
            {activeGraphicTool
              ? LAYOUT_GRAPHIC_TOOLS.find((tool) => tool.type === activeGraphicTool)?.label
              : 'Graphics and Text'}
            <Icon name={openGallery === 'graphics' ? 'expand_less' : 'expand_more'} size={16} decorative />
          </button>
          {openGallery === 'graphics' ? (
            <div
              role="dialog"
              aria-label="Graphics and Text tools"
              data-testid="layout-graphics-gallery"
              style={menuPanel(GRAPHICS_PANEL_WIDTH, graphicsPanel.shiftLeftPx)}
            >
              {LAYOUT_GRAPHIC_CATEGORIES.map((category) => (
                <div key={category} role="group" aria-label={category} style={{ paddingBottom: tokens.space[3] }}>
                  <div style={{ ...groupLabel, color: tokens.color.ink.primary, fontWeight: tokens.font.weight.semibold }}>
                    {category}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: tokens.space[1] }}>
                    {LAYOUT_GRAPHIC_TOOLS.filter((tool) => tool.category === category).map((tool) => (
                      <button
                        key={tool.type}
                        type="button"
                        aria-label={`Draw ${tool.label}`}
                        aria-pressed={activeGraphicTool === tool.type}
                        onClick={() => {
                          onSelectGraphicTool(tool.type);
                          setOpenGallery(null);
                        }}
                        style={{ ...tile, minHeight: 76, background: activeGraphicTool === tool.type ? tokens.color.accent.subtle : tokens.color.canvas.light }}
                      >
                        <span aria-hidden style={{ width: 54, height: 28, display: 'block' }}>
                          <LayoutGraphic element={createLayoutGraphicElement(tool.type, { rect: { x: 0, y: 0, width: 54, height: 28 } })} preview />
                        </span>
                        <span>{tool.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2], paddingTop: tokens.space[2], borderTop: tokens.border.thin, fontSize: tokens.font.size.sm }}>
                <input
                  type="checkbox"
                  aria-label="Keep last graphic tool active"
                  checked={keepGraphicToolActive}
                  onChange={(event) => onKeepGraphicToolActive(event.target.checked)}
                />
                Keep last tool active
              </label>
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <div style={groupLabel}>Picture</div>
        <div ref={picturePanel.anchorRef} style={{ position: 'relative' }}>
          <button
            type="button"
            aria-label="Open picture gallery"
            aria-haspopup="menu"
            aria-expanded={openGallery === 'picture'}
            onClick={() => {
              setPictureError(null);
              toggle('picture');
            }}
            style={{
              ...chip(openGallery === 'picture'),
              display: 'flex',
              alignItems: 'center',
              gap: tokens.space[2],
            }}
          >
            <Icon name="image" size={16} decorative />
            Picture
            <Icon
              name={openGallery === 'picture' ? 'expand_less' : 'expand_more'}
              size={16}
              decorative
            />
          </button>
          {openGallery === 'picture' ? (
            <div
              role="menu"
              aria-label="Insert picture"
              data-testid="layout-picture-gallery"
              style={menuPanel(PICTURE_PANEL_WIDTH, picturePanel.shiftLeftPx)}
            >
              <input
                ref={pictureFileRef}
                type="file"
                accept={PICTURE_ACCEPT}
                aria-label="Choose picture file"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  void layoutPictureSourceFromFile(file)
                    .then((source) => {
                      setPictureError(null);
                      setOpenGallery(null);
                      onPictureSource(source);
                    })
                    .catch((cause: unknown) => {
                      setPictureError(cause instanceof Error ? cause.message : 'Unable to import picture');
                    });
                }}
              />
              <button
                type="button"
                role="menuitem"
                aria-label="Insert picture from local or network path"
                onClick={() => pictureFileRef.current?.click()}
                style={{ ...chip(), width: '100%', justifyContent: 'flex-start', marginBottom: tokens.space[2] }}
              >
                <Icon name="upload_file" size={16} decorative /> From local or network path
              </button>
              <label
                style={{
                  display: 'grid',
                  gap: tokens.space[1],
                  fontSize: tokens.font.size.xs,
                  color: tokens.color.ink.secondary,
                }}
              >
                From URL
                <input
                  aria-label="Picture URL"
                  value={pictureUrl}
                  onChange={(event) => setPictureUrl(event.target.value)}
                  style={{
                    minHeight: tokens.density.rowDense,
                    padding: `0 ${tokens.space[2]}px`,
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.input,
                    background: tokens.color.surface[0],
                    color: tokens.color.ink.primary,
                  }}
                />
              </label>
              <button
                type="button"
                role="menuitem"
                aria-label="Insert picture from URL"
                onClick={() => {
                  void layoutPictureSourceFromUrl(pictureUrl)
                    .then((source) => {
                      setPictureError(null);
                      setOpenGallery(null);
                      onPictureSource(source);
                    })
                    .catch((cause: unknown) => {
                      setPictureError(cause instanceof Error ? cause.message : 'Unable to import picture');
                    });
                }}
                style={{ ...chip(), width: '100%', justifyContent: 'flex-start', marginTop: tokens.space[2] }}
              >
                <Icon name="link" size={16} decorative /> Insert from URL
              </button>
              {pictureError ? (
                <div role="alert" style={{ marginTop: tokens.space[2], color: tokens.color.semantic.danger, fontSize: tokens.font.size.xs }}>
                  {pictureError}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <div style={groupLabel}>Export</div>
        <div style={galleryStyle}>
          <button type="button" aria-label="Export layout PNG" onClick={onExportPng} style={chip()}>
            <Icon name="download" size={16} decorative /> PNG
          </button>
          <button type="button" aria-label="Print layout" onClick={onPrint} style={chip()}>
            <Icon name="print" size={16} decorative /> Print
          </button>
        </div>
      </div>
    </div>
  );
}
